/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { UntypedFormArray } from "@angular/forms";
import { MatTableDataSource } from "@angular/material/table";
import { Observable, Subject } from "rxjs";
import { AbstractObject } from "@src/app/model/abstract-object";
import { Field } from "@src/app/shared/field/Field";
import { AppFormControl } from "@src/app/shared/form/app-form-control";
import { GridRow } from "@src/app/shared/grid/grid-row";
import { GridComponent } from "@src/app/shared/grid/grid.component";
import { GridField } from "@src/app/shared/grid/grid-field";

export class GridControl extends UntypedFormArray {
    static readonly rowsPerPage = 50;

    rowFactory: (item: AbstractObject) => Field[];
    objFactory: () => Observable<AbstractObject>;
    rowNomenclator(row: GridRow) {
        if (row.get('name')) {
            return row.get('name').value;
        } else if (row.get('title')) {
            return row.get('title').value;
        }
        const visibleFields = row.fields.filter(f => f.visible.form);
        let ret = "";
        for (const f of visibleFields) {
            if (f.name !== 'memo') {
                if (ret.length > 0) {
                    ret += ', ';
                }
                ret += f.label + ': ' + f.getFormattedValue(row.focus) // f.getDisplayValue(o);
            }
        }
        return ret;
    }

    data = new Subject<GridRow[]>();
    dataSource = new MatTableDataSource<GridRow>();
    columns: Field[] = [];
    columnNames: string[] = [];
    field: GridField;
    hasFooter = false;
    totals = new Map<string, number>();
    displayMode?: 'grid' | 'accordion' = 'accordion';
    newOptionText = '';
    pageIndex = 0;
    showAllRows = false;

    constructor(fld: GridField) {
        super([], fld.config.gridValidator, fld.config.gridASyncValidator);
        this.rowFactory = fld.config.rowFactory;
        this.objFactory = fld.config.objFactory;
        if (!fld.config.displayMode) {
            this.displayMode = 'grid';
        } else {
            this.displayMode = fld.config.displayMode
        }
        if (fld.config.rowNomenclator) {
            this.rowNomenclator = fld.config.rowNomenclator;
        }
        if (fld.config.newOptionText) {
            this.newOptionText = fld.config.newOptionText;
        }
        this.field = fld;
        this.field.formControlFactory = GridComponent.createComponent;
        if (fld.config.hasFooter) {
            this.hasFooter = fld.config.hasFooter;
        }
    }
    refresh() {
        if (this.columns.length === 0) {
            this.setColumns();
        }
        this.pageIndex = Math.min(this.pageIndex, this.pageCount() - 1);
        this.dataSource.data = this.pagedRows();
        //this.data.next(this.controls as GridRow[]);
    }

    gridRows(): GridRow[] {
        return (this.controls as GridRow[]);
    }

    pagedRows(): GridRow[] {
        if (this.showAllRows) {
            return this.gridRows();
        }
        return this.gridRows().slice(this.firstRowOnPage(), this.firstRowOnPage() + GridControl.rowsPerPage);
    }

    isPaged(): boolean {
        return !this.showAllRows && this.length > GridControl.rowsPerPage;
    }

    pageCount(): number {
        return Math.max(1, Math.ceil(this.length / GridControl.rowsPerPage));
    }

    firstRowOnPage(): number {
        return this.pageIndex * GridControl.rowsPerPage;
    }

    lastRowOnPage(): number {
        return Math.min(this.firstRowOnPage() + GridControl.rowsPerPage, this.length);
    }

    showPage(pageIndex: number) {
        this.pageIndex = pageIndex;
        this.refresh();
    }

    showLastPage() {
        this.showPage(this.pageCount() - 1);
    }

    clear() {
        super.clear();
        this.totals.clear();
        this.pageIndex = 0;
    }

    setColumns() {
        const controlArray = this.rowFactory(null);
        const fieldArray = [];
        const nameArray = [];
        controlArray.forEach((field) => {
            fieldArray.push(field);
            if (field.visible.computer || field.visible.phone) {
                nameArray.push(field.name);
            }
        });
        this.columns = fieldArray;
        this.columnNames = nameArray;
    }

    public addRow(item: AbstractObject, refreshGrid = true, readonly: boolean): GridRow {
        const arry = this.rowFactory(item);
        const newRow = new GridRow(item, {}, arry);
        this.push(newRow);
        arry.forEach(cellField => {
            if (cellField.control) {
                console.error('Row Factories should not create fields with controls, are fields being reused instead of manufactured?'
                    , cellField, arry);
            }
            const ctl = cellField.makeControl() as AppFormControl;
            newRow.addControl(cellField.name, ctl);
            ctl.isGridCell = true;
            cellField.setValue(item, readonly);
        });
        this.recalculateTotals();
        if (refreshGrid) {
            this.refresh();
        }
        return newRow;
    }

    recalculateTotals() {
        let setZeroDone = false;
        for (const row of (this.controls as GridRow[])) {
            for (const fld of row.fields) {
                if (!setZeroDone) {
                    this.totals.set(fld.name, 0);
                }
                if (fld.type === 'number') {
                    this.totals.set(fld.name, this.totals.get(fld.name) + fld.control.value);
                } else {
                    this.totals.set(fld.name, this.totals.get(fld.name) + 1);
                }
            }
            setZeroDone = true;
        }
    }

    removeGridRow(gridRow: GridRow) {
        let removed = false;
        this.controls.forEach((control, index) => {
            if (gridRow === control) {
                this.removeAt(index);
                removed = true;
            }
        });
        if (!removed) {
            console.warn('Could not find grid row to remove it ', { gridControl: this, gridRow });
        }
        this.refresh();
    }

    getFormValue(getAll = false): AbstractObject[] {

        const formValue: AbstractObject[] = [];
        this.gridRows().forEach(gridRow => {
            formValue.push(gridRow.getFormValue(getAll));
        });

        return formValue;
    }
}
